import { scrollShadow } from '@nextui-org/react';
import { useCallback, useRef } from 'react';

// Scroll cap plus fading edges for a NextUI DropdownMenu, matching the
// ScrollShadow that NextUI's Select wraps around its listbox (login tenant
// selector). DropdownMenu can't be wrapped in ScrollShadow, so this sets the
// same data-*-scroll attributes on the menu element that scrollShadow() styles.
//
// The cap is the viewport, not a fixed height: the menu opens below the 4rem
// sidebar header, so it only scrolls when the list wouldn't fit on screen.
//
// outline-none: a scrollable container is focusable in Chrome, and the menu
// would otherwise show the browser focus ring when it opens.
const MENU_CLASS = [
  scrollShadow(),
  '[--scroll-shadow-size:40px] max-h-[calc(100dvh-6rem)] outline-none',
].join(' ');

export const useMenuScrollShadow = () => {
  const cleanupRef = useRef<(() => void) | null>(null);

  // A callback ref rather than state: NextUI re-attaches the menu ref on every
  // menu render (null, then the element), and state would re-render the parent
  // each time.
  const ref = useCallback((element: HTMLElement | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    if (!element) return;

    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = element;
      const hasTop = scrollTop > 1;
      const hasBottom = scrollTop + clientHeight < scrollHeight - 1;
      element.dataset.topScroll = String(hasTop && !hasBottom);
      element.dataset.bottomScroll = String(hasBottom && !hasTop);
      element.dataset.topBottomScroll = String(hasTop && hasBottom);
    };

    update();
    element.addEventListener('scroll', update, { passive: true });
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(element);

    cleanupRef.current = () => {
      element.removeEventListener('scroll', update);
      resizeObserver.disconnect();
    };
  }, []);

  return { ref, className: MENU_CLASS };
};
